import axios from 'axios';
import normalize from 'Utils/normalizer';
import { map, omitBy, isEmpty, isNil, flatten } from 'lodash';
import moment from "moment";
import { errorHandler } from '../utils';
import { apiURL } from '../helpers';

import useSession from "Hooks/session.hook";
import { OrderStatusTypes, PaymentStatusTypes } from 'Components/order/purchase-list/purchase-list';
import { PurchasedVoucher, PurchasedVoucherAssignedTo } from 'src/models/telemed-voucher';
import { QuotationRequest } from 'src/models/quotation-request';
import { ItemType } from '../models/quotation';

interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface Payment {
  subTotal: string;
  discount: string;
  shippingFee: string;
  adjustment: string;
  total: string;
}

interface Pagination {
  offset: number,
  limit: number
}

interface Sorting {
  sortBy: string,
  mode: "ASC" | "DESC"
}

interface Filter {
  _id?: string,
  orderedBy?: string,
  orderCode?: string,
  fromDate?: moment.Moment,
  toDate?: moment.Moment,
  supplierID?: string,
  receiverID?: string,
  status?: Array<any>,
  withVoucher?: Boolean,
}

interface PaymentListFilter { 
  payer?: string;
  status?: PaymentStatusTypes[];
  orderStatus?: OrderStatusTypes[];
  orderType?: string[];
}

interface QuotationRequestListFilter { 
  requestedBy?: string;
  // requestTypes?: string;
  sourceSite?: string;
}

interface IData {
  /*receiverID: string;
  orderedBy: string;
  orderDate: moment.Moment;
  supplierID: string;
  items: any[],
  shippingAddress: Address,
  reqStatus: string,
  paymentDetails: Payment,
  remarks: string,
  status: string,*/
  orders: any[],
  paymentTotals: any
}


export const fetchOrderById = async (filters: Filter, pagination?: any, sorting?: Sorting) => {
  const url = apiURL('/ecom/order/fetch');
  const { accessToken, profile } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: omitBy({ filters: { ...filters, orderedBy: profile?._id }, pagination, sorting }, isNil),
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return !isEmpty(response.data.order) ? normalize.response.order(response.data.order) : [];
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchOrderById(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const fetchOrderList = async (filters: Filter, pagination?: any, sorting?: Sorting) => {
  const url = apiURL('/ecom/order/list');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: omitBy({ filters: filters, pagination, sorting }, isNil),
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return !isEmpty(response.data.orders) ?
      map(response.data.orders, order => {
        return normalize.response.order(order)
      }) : [];
    // return !isEmpty(response.data.orders) ? response.data.orders : [];
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchOrderList(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const fetchPaymentList = async (filters: PaymentListFilter, pagination?: any, sorting?: Sorting) => {
  const url = apiURL('/ecom/payment/list');
  const { accessToken } = useSession();
  
  try {
    const response = await axios({
        method: 'POST',
        url: url,
        data: omitBy({ filters: filters, pagination, sorting }, isNil),
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })

    return !isEmpty(response.data.payments) ?
      map(response.data.payments, payment => {
        return {
          ...payment,
          orders: payment.orderData.map(normalize.response.order),
        };
      }) : [];
    // return !isEmpty(response.data.orders) ? response.data.orders : [];
  }
  catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchPaymentList(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const createOrder = async (data: IData) => {
  const url = apiURL('/ecom/order/create');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        orders: map(data.orders, orderObj => {
          return normalize.model.order(orderObj)
        }), paymentTotals: data.paymentTotals
      },
      // data: data,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    if (!isEmpty(response.data.order)) { // To use for create payment
      const orderID = response.data.order?._id;
    }

    return response.data;
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createOrder(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}
export const fetchOrder = async (id: string) => {
  const url = apiURL('/ecom/order/fetch');
  const { accessToken, profile } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { filters: { _id: id, orderedBy: profile?._id } },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return normalize.response.order(response.data.order);
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchOrder(id);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const fetchDispatchDetails = async (trackingId: string) => {
  const url = apiURL('/ecom/delivery/updates/fetch');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: { trackingNumber: trackingId },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return response.data?.updates || [];
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchDispatchDetails(trackingId);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const updateOrderStatus = async (updateData: any) => {
  const url = apiURL('/ecom/order/status/update');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: updateData,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return normalize.response.order(response.data.order);
  } catch (err) {
    console.error("Update order error:", err);
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateOrderStatus(updateData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const updateOrderItems = async (updateData: any) => {
  const url = apiURL('/ecom/order/items/update');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: updateData,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  } catch (err) {
    console.error("Update order item error:", err);
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateOrderItems(updateData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const assignVouchers = async (data: { orderID: string, assignedTo: PurchasedVoucherAssignedTo, vouchers: PurchasedVoucher[]}) => {
  const url = apiURL('/dem/order/voucher/assign/update');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: normalize.model.voucherAssign(data),
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await assignVouchers(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const addNewAddress = async (updateData: any) => {
  const url = apiURL('/dem/profile/update');
  const { accessToken } = useSession();
  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: updateData,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return response.data.profile;
  } catch (err) {
    console.error("Update order error:", err);
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await addNewAddress(updateData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}


export const fetchProfileInfo = async () => {
  const url = apiURL('/dem/profile/fetch');
  const { accessToken } = useSession();
  const { profile } = useSession();

  try {
    const profileID = profile._id;

    const response = await axios({
      method: 'POST',
      url: url,
      data: { profileID: profileID },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    return response.data.profile ? response.data.profile : [];
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchProfileInfo();
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const fetchCartList = async () => {
  const url = apiURL('/ecom/cart/fetch');
  const { accessToken } = useSession();
  const { profile } = useSession();

  try {
    const userID = profile._id;
    const response = await axios({
      method: 'POST',
      url: url,
      data: { userID: userID },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    let obj = normalize.response.cart(response.data);
    return obj;
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchCartList();
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}


export const addToCartList = async (data: any) => {
  const url = apiURL('/ecom/cart/create');
  const { accessToken } = useSession();
  const { profile } = useSession();
  let list = normalize.model.cart(data.items);

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        cart: {
          userID: profile._id,
          items: list.items
        }
      },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })

    return response;
  }
  catch (err) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await addToCartList(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(err.message)
      return { error: { message: err.message } };
    }
  }
}

export const updateCartItem = async (updateCartData: any) => {
  const url = apiURL('/ecom/cart/update');
  const { accessToken } = useSession();
  const session = useSession();
  const {profile} = useSession();
  let list = normalize.model.cart(updateCartData.items)

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: {
        cartID: updateCartData.cardId,
        cart: {
          userID: profile._id,
          items: list.items
        }
      },
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  } catch (error) {
    if (!isEmpty(error)) {
      const callback = async () => await updateCartItem(updateCartData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(error.message)
      return { error: { message: error.message } };
    }
  }
}


export const deleteCartItem = async (cartData) => {
  const url = apiURL('/ecom/order/items/delete');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: cartData,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  }
  catch (error) {
    if (!isEmpty(error)) {
      const callback = async () => await deleteCartItem(cartData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(error.message)
      return { error: { message: error.message } };
    }
  }
}


export const cancelOrder = async (cancelObj) => {
  const url = apiURL('/ecom/order/status/update');
  const { accessToken } = useSession();

  try {
    // fetch order to see whether can be cancelled
    const { orderID } = cancelObj;
    const fetchedOrderObj = await fetchOrder(orderID);
    if (fetchedOrderObj.error) {
      return fetchedOrderObj
    }
    // there are redeemed voucher(s) under this order
    const someVouchersAreRedeemed = !isEmpty(flatten(fetchedOrderObj.items.map(oi => oi.vouchers).
                                      filter(v => !isNil(v))).
                                      filter((v: any) => v.status === 'REDEEMED'));

    if (someVouchersAreRedeemed) {
      return {
        error: {
          message: 'This order cannot be cancelled because some of the vouchers have been redeemed.'
        }
      }
    }

    const response = await axios({
      method: 'POST',
      url: url,
      data: cancelObj,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  } catch (error) {
    if (!isEmpty(error)) {
      const callback = async () => await cancelOrder(cancelObj);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(error.message)
      return { error: { message: error.message } };
    }
  }
}

export const downloadReceipt = async (obj) => {
  const url = apiURL('/ecom/order/receipt/fetch');
  const { accessToken } = useSession();

  try {
    const response = await axios({
      method: 'POST',
      url: url,
      data: obj,
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
    return response.data;
  } catch (error) {
    if (!isEmpty(error)) {
      const callback = async () => await downloadReceipt(obj);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    }
    else {
      console.log(error.message)
      return { error: { message: error.message } };
    }
  }
}
